import { brandTextColor, brandingFontFamilies, contrastRatio, formBrandClasses, formBrandStyle, formLogo, resolveBrandAppearance, resolveBrandingTheme, safeLogoUrl } from '@moxie/branding';

export function preferredBrandAppearance(preference = 'automatic', systemDark = false) {
	if (preference === 'always') return 'dark';
	if (preference === 'never') return 'light';
	return systemDark ? 'dark' : 'light';
}

export function resolveAppearanceBranding(branding, preferred = 'light') {
	if (!branding || typeof branding !== 'object') return null;
	return resolveBrandingTheme(branding, resolveBrandAppearance(branding, preferred));
}

export function documentBrandAppearance(branding, preferred = 'light') {
	const appearance = branding && branding.appearance;
	return ['light', 'dark', 'both'].includes(appearance)
		? resolveBrandAppearance(branding, preferred)
		: 'light';
}

export function resolveDocumentBranding(branding, preferred = 'light') {
	if (!branding || typeof branding !== 'object') return null;
	return resolveBrandingTheme(branding, documentBrandAppearance(branding, preferred));
}

export function resolvePrintBranding(branding) {
	if (!branding || typeof branding !== 'object') return null;
	return resolveBrandingTheme(branding, 'light');
}

export function resolvePortalBranding(domain, theme = 'light') {
	const branding = domain && domain.defaultBranding;
	return resolveAppearanceBranding(branding, theme);
}

export function ticketFormBranding(template, domain) {
	return (template && template.branding) || (domain && domain.defaultBranding) || null;
}

export function portalBrandingActive(branding, route) {
	const path = route && route.path;
	const documentOwned = path && (
		path.startsWith('/document/') ||
		path === '/invoice' ||
		path.startsWith('/invoice/') ||
		path === '/payment' ||
		path === '/discovery'
	);
	return Boolean(branding && !documentOwned);
}

export function allowsPortalThemeChoice(domain) {
	const appearance = domain && domain.defaultBranding && domain.defaultBranding.appearance;
	return !appearance || appearance === 'both';
}

export function portalBrandStyle(branding) {
	if (!branding) return {};
	const rowBackground = contrastRatio(branding.colors.background, '#FFFFFF') > contrastRatio(branding.colors.background, '#000000')
		? '#121212'
		: '#FFFFFF';
	return {
		...formBrandStyle(branding),
		'--v-gray_10-base': 'var(--brand-background)',
		'--v-gray_20-base': 'color-mix(in srgb, var(--brand-primary) 5%, var(--brand-surface))',
		'--v-gray_30-base': 'color-mix(in srgb, var(--brand-border) 35%, var(--brand-surface))',
		'--v-gray_50-base': 'var(--brand-border)',
		'--v-gray_60-base': 'var(--brand-surfaceMuted)',
		'--v-gray_70-base': 'var(--brand-surfaceMuted)',
		'--v-gray_80-base': 'var(--brand-surfaceText)',
		'--v-gray_90-base': 'var(--brand-surfaceText)',
		'--portal-table-row-background': rowBackground,
		'--portal-table-row-text': brandTextColor(branding, branding.colors.text, rowBackground),
		'--portal-table-row-muted': brandTextColor(branding, branding.colors.muted, rowBackground),
	};
}

export function portalBrandClasses(branding) {
	return branding ? ['portal-brand-kit', ...formBrandClasses(branding)] : [];
}

export function portalLogo(branding, fallback, background) {
	return branding ? formLogo(branding, fallback, background) : fallback;
}

export function portalSquareLogo(branding, background) {
	const logos = branding && branding.logos;
	const dark = background && contrastRatio(background, '#FFFFFF') > contrastRatio(background, '#000000');
	return safeLogoUrl(logos && (dark ? logos.squareDark || logos.square : logos.square || logos.squareDark));
}

export function portalFavicon(domain, background) {
	return portalSquareLogo(domain && domain.defaultBranding, background) ||
		safeLogoUrl(domain && domain.customBranding && domain.customBranding.favicon) || null;
}

export function applyPortalFavicon(domain, background) {
	const favicon = portalFavicon(domain, background);
	if (!favicon) return;
	let link = document.querySelector('link[rel~="icon"]');
	if (!link) {
		link = document.createElement('link');
		link.rel = 'icon';
		document.head.appendChild(link);
	}
	link.href = favicon;
}

export function portalFont(branding, customBranding) {
	return branding ? branding.typography.bodyFamily : (customBranding && customBranding.font) || 'Inter';
}

export function portalGoogleFontFamilies(branding, accountFonts) {
	const customFonts = new Set((accountFonts || []).map((font) => font.fontName));
	return brandingFontFamilies(branding).filter((font) => !customFonts.has(font));
}
